// src/services/apiService.js
import instance, { globalApi } from "../api/axios"; // Import các instances chuẩn Enterprise phòng khi dùng

const CHATBOT_BASE_URL =
  process.env.REACT_APP_CHATBOT_API_URL || "https://apiqrpay-dev.tomotek.vn";
const ADMIN_CUSTOMER_TIMEOUT_CONFIG = { timeout: 60000 };
const pickBody = (payload) => {
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([, value]) => value !== undefined && value !== null && value !== "",
    ),
  );
};

const merchantBody = (merchantId, extra = {}) => {
  if (merchantId && typeof merchantId === "object") {
    return pickBody(merchantId);
  }

  return pickBody({ merchant_id: merchantId, ...extra });
};

// --- NHÓM 1: AUTHENTICATION ---
export const authService = {
  login: (data) => {
    return globalApi.post("/auth/login", data);
  },
};

// --- NHÓM 2: CUSTOMER (KHÁCH HÀNG) ---
export const customerService = {
  // Lấy danh sách khách hàng (Admin)
  getList: (payload) =>
    globalApi.post(
      "/api/admin/customers/list",
      pickBody(payload || {}),
      ADMIN_CUSTOMER_TIMEOUT_CONFIG,
    ),

  // Lấy danh sách khách hàng (Admin) - New
  getReport: (payload) => globalApi.post(
    "/api/admin/customers/report",
    pickBody(payload || {})
  ),  

  // Lấy chi tiết khách hàng
  getDetail: (account, bankCode) =>
    globalApi.post("/api/admin/customers/detail", { account, bankCode: bankCode }),

  // Lấy lịch sử giao dịch của khách hàng
  getTransactions: (account, payload = {}) =>
    globalApi.post(
      "/api/admin/customers/transactions",
      pickBody({ account, ...payload }),
    ),

  // Lấy thống kê tổng quan khách hàng (Global Stats)
  getGlobalCustomerStats: () =>
    globalApi.post(
      "/api/admin/customers/stats",
      undefined,
      ADMIN_CUSTOMER_TIMEOUT_CONFIG,
    ),

  // API Customer Totals
  getTotals: (merchantId, limit, offset, keyword) =>
    instance.post("/api/customers/totals", {
      merchant_id: merchantId,
      limit: limit,
      offset: offset,
      keyword: keyword || undefined,
    }),

  // -- CUSTOMER 360 ANALYTICS --
  getInsightCustomerStats: (merchantId) =>
    instance.post("/api/customers/stats", { merchant_id: merchantId }),
  getActiveCustomers: (payload) =>
    instance.post(
      "/api/customers/analytics/active-customers",
      pickBody(payload || {}),
      { timeout: 8000 },
    ),
  getFrequency: (payload) =>
    instance.post(
      "/api/customers/analytics/frequency",
      pickBody(payload || {}),
      { timeout: 8000 },
    ),
  getArpc: (payload) =>
    instance.post("/api/customers/analytics/arpc", pickBody(payload || {}), {
      timeout: 8000,
    }),
  getCustomerTypes: (payload) =>
    instance.post(
      "/api/customers/analytics/customer-types",
      pickBody(payload || {}),
      { timeout: 8000 },
    ),
  getRevenueConcentration: (payload) =>
    instance.post(
      "/api/customers/analytics/revenue-concentration",
      pickBody(payload || {}),
      { timeout: 8000 },
    ),
  getCohorts: (payload) =>
    instance.post("/api/customers/analytics/cohorts", pickBody(payload || {}), {
      timeout: 8000,
    }),
  getRfm: (payload) =>
    instance.post("/api/customers/analytics/rfm", pickBody(payload || {}), {
      timeout: 8000,
    }),
  getChurnPrediction: (payload) =>
    instance.post(
      "/api/customers/analytics/churn-prediction",
      pickBody(payload || {}),
      { timeout: 8000 },
    ),
  getRecency: (payload) =>
    instance.post("/api/customers/analytics/recency", pickBody(payload || {}), {
      timeout: 8000,
    }),
};

// --- NHÓM 3: MERCHANT (CỬA HÀNG) ---
export const merchantService = {
  // Lấy thông tin merchant theo danh sách ID
  getInfo: (terminalIDs) =>
    globalApi.post("/api/merchant/info", { terminalIDs }),

  // Lấy giao dịch theo ngày
  getTransactionByDay: (merchantId, date) =>
    instance.post("/api/merchant/transaction-by-day", {
      merchant_id: merchantId,
      date,
    }),

  // Lấy doanh thu hàng ngày
  getDailyRevenue: (merchantId, month) =>
    instance.post("/api/merchant/daily-revenue", {
      merchant_id: merchantId,
      month,
    }),
  getWorkspace: (payload) =>
    instance.post("/api/merchant/list", pickBody(payload || {}), {
      timeout: 8000,
    }),

  getRevenueReport: (merchantId, month) =>
    instance.post(
      "/api/merchant/revenue-report",
      merchantBody(merchantId, { month }),
    ),

  getGrowthScaleMetric: (merchantId, startDate, endDate) =>
    instance.post("/growth-scale/metrics", {
      merchant_id: merchantId,
      start_date: startDate,
      end_date: endDate,
    }),

  // API AI Report (URL khác domain chính)
  getAiReportLatest: (payload) =>
    instance.post("/api/ai/daily-report/latest", pickBody(payload || {})),

  getCustomerRetention: (merchantId, startDate, endDate) =>
    instance.post(
      "/api/customers/analytics/customer-retention",
      merchantBody(merchantId, {
        start_date: startDate,
        end_date: endDate,
      }),
    ),

  getCustomerMonthlyReport: (merchantId, month) =>
    instance.post("/api/customer/monthly-report", {
      merchant_id: merchantId,
      month,
    }),

  getSegmentSummary: (merchantId) =>
    instance.post(
      "/api/customers/analytics/segments-summary",
      merchantBody(merchantId),
    ),
};

// --- NHÓM 4: DASHBOARD & REPORT ---
export const dashboardService = {
  getOverview: () => globalApi.get("/api/dashboard/overview"),
};

export const analyticsService = {
  getPaymentMethodProportion: (payload) =>
    instance.post(
      "/api/analytics/payment-method-proportion",
      pickBody(payload || {}),
      { timeout: 8000 },
    ),

  getBankProportion: (payload) =>
    instance.post("/api/analytics/bank-proportion", pickBody(payload || {}), {
      timeout: 8000,
    }),

  getIssuerShare: (payload) =>
    instance.post("/api/analytics/issuer-share", pickBody(payload || {}), {
      timeout: 8000,
    }),

  getHourlyHeatmap: (payload) =>
    instance.post("/api/analytics/hourly-heatmap", pickBody(payload || {}), {
      timeout: 8000,
    }),

  getServiceMethodMix: (payload) =>
    instance.post(
      "/api/analytics/service-method-mix",
      pickBody(payload || {}),
      { timeout: 8000 },
    ),
};

// --- NHÓM 5: CHATBOT ---
export const chatbotService = {
  // Lấy lịch sử chat
  getHistory: (userId, cursor = null, limit = 10) => {
    return globalApi.get(`${CHATBOT_BASE_URL}/api/v1/chatbot/history`, {
      headers: { userId: userId },
      params: { limit, ...(cursor && { cursor }) },
    });
  },

  // Gửi tin nhắn và nhận Stream Data
  // (Do logic stream của Server-Sent Events đòi hỏi dùng Web Stream API, gọi bằng Fetch API tuần tự)
  streamChat: (userId, question) => {
    return fetch(`${CHATBOT_BASE_URL}/api/v1/chatbot/stream`, {
      method: "POST",
      headers: {
        userId: userId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ question }),
    });
  },
};
